import React, { useState, useEffect } from 'react';
import InlineAlert from '../../components/InlineAlert';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { restaurantService } from '../../services/restaurants';
import { Order } from '../../types';
import ClientTablesReservationModal from '../../components/ClientTablesReservationModal';

const OrdersHistoryPage: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restaurant, setRestaurant] = useState<any>(null);
  const [pageAlert, setPageAlert] = useState<{ type: 'info' | 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [showTablesModal, setShowTablesModal] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});
  const [isMobileView, setIsMobileView] = useState(false);
  
  useEffect(() => {
    if (restaurantId && user?.uid) {
      loadOrders();
      loadRestaurantInfo();
    }
  }, [restaurantId, user?.uid]);

  useEffect(() => {
    const onResize = () => {
      const mobile = typeof window !== 'undefined' ? window.innerWidth < 640 : false;
      setIsMobileView(mobile);
    };
    onResize();
    if (typeof window !== 'undefined') window.addEventListener('resize', onResize);
    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
    };
  }, []);

  const loadOrders = async () => {
    if (!restaurantId || !user?.uid) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Cargando historial de pedidos...', { restaurantId, userId: user.uid });
      
      const clientOrders = await restaurantService.getOrdersByRestaurant(user.uid, restaurantId);
      
      console.log('✅ Pedidos cargados:', clientOrders.length);
      setOrders(clientOrders);
      
    } catch (error) {
      console.error('❌ Error cargando historial de pedidos:', error);
      setError('No se pudieron cargar los pedidos. Verifica tu conexión.');
    } finally {
      setLoading(false);
    }
  };

  const loadRestaurantInfo = async () => {
    if (!restaurantId) return;

    try {
      const restaurantData = await restaurantService.getRestaurantById(restaurantId);
      if (restaurantData) {
        setRestaurant(restaurantData);
      }
    } catch (error) {
      console.error('❌ Error cargando información del restaurante:', error);
    }
  };

  const openTablesModal = () => setShowTablesModal(true);

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'created':
        return 'bg-orange-100 text-orange-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'preparing':
        return 'bg-blue-100 text-blue-800';
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'delivered':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: Order['status']) => {
    switch (status) {
      case 'created':
        return 'Creado';
      case 'pending':
        return 'Pendiente';
      case 'preparing':
        return 'Preparando';
      case 'ready':
        return 'Listo';
      case 'delivered':
        return 'Entregado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-[#2C4358] rounded-full animate-spin mx-auto" style={{ animationDelay: '0.5s' }}></div>
          </div>
          <h3 className="text-lg font-bold text-[#2C4358] mb-2">Cargando historial...</h3>
          <p className="text-[#85929e]">Buscando tus pedidos anteriores</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">😔</div>
          <h3 className="text-lg font-bold text-[#2C4358] mb-2">Error al cargar pedidos</h3>
          <p className="text-[#85929e] mb-6">{error}</p>
          <button
            onClick={loadOrders}
            className="bg-[#22B6B2] text-white px-6 py-3 rounded-lg hover:bg-[#1a9a96] transition-colors"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6 sm:mb-8">
                  {restaurant && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6">
              {/* Información del restaurante */}
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {restaurant.name}
                </h3>
                
                {restaurant.description && (
                  <p className="text-gray-600 text-sm mb-3">
                    {restaurant.description}
                  </p>
                )}
                
                {restaurant.address && (
                  <p className="text-gray-500 text-sm flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {restaurant.address}
                  </p>
                )}
              </div>
              
              {/* Botones de acción */}
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                {restaurant.phone && (
                  <div className="flex space-x-2">
                    {/* Botón WhatsApp */}
                    <button
                      onClick={() => {
                        const phone = restaurant.phone.replace(/\s/g, '');
                        const message = `Hola, me gustaría hacer una consulta sobre ${restaurant.name}`;
                        const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
                        window.open(whatsappUrl, '_blank');
                      }}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                      title="Contactar por WhatsApp"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                      <span className="text-sm">WhatsApp</span>
                    </button>
                    
                    {/* Botón llamada */}
                    <button
                      onClick={() => {
                        const phone = restaurant.phone.replace(/\s/g, '');
                        window.open(`tel:${phone}`, '_blank');
                      }}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                      title="Llamar al restaurante"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="text-sm">Llamar</span>
                    </button>
                  </div>
                )}
                
                {/* Botón disponibilidad de mesas */}
                <button
                  onClick={openTablesModal}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  title="Ver disponibilidad de mesas"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="text-sm">Ver mesas / Reservar</span>
                </button>
              {/* Botón para ver el menú del restaurante */}
              <button
                onClick={() => navigate(`/menu?restaurantId=${restaurantId}`)}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 mt-2"
                title="Ver menú del restaurante"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className="text-sm">Ver menú</span>
              </button>
              </div>
            </div>
          </div>
        )}
        </div>

                {/* Header del historial */}
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Historial de Pedidos
          </h2>
          <p className="text-gray-600">
            Revisa todos tus pedidos anteriores
          </p>
        </div>
        {/* Alerta informativa */}
        {pageAlert && (
          <div className="max-w-xl mx-auto mb-4">
            <InlineAlert type={pageAlert.type} message={pageAlert.text} onClose={() => setPageAlert(null)} />
          </div>
        )}
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg border border-gray-200 p-8 max-w-md mx-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                No hay pedidos aún
              </h3>
              <p className="text-gray-600 mb-6">
                {restaurant 
                  ? `Aún no has visitado ${restaurant.name}. ¡Haz tu primera visita!`
                  : 'Aún no has visitado este restaurante. ¡Puedes explorar su menú y hacer tu reservación!'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate(`/menu?restaurantId=${restaurantId}`)}
                  className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Explorar menú
                </button>
                <button
                  onClick={() => navigate('/client')}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-900 px-4 py-2 rounded-lg transition-colors"
                >
                  Ver Otros Restaurantes
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Header del pedido */}
                <button
                  className="w-full text-left p-4 sm:p-6 border-b border-gray-100"
                  onClick={() => setExpandedOrders(prev => ({ ...prev, [order.id]: !(prev[order.id]) }))}
                  aria-expanded={(expandedOrders[order.id] ?? !isMobileView) ? true : false}
                >
                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                          Pedido #{order.id.slice(-8)}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
                        {formatDate(order.orderDate)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-base sm:text-lg font-semibold text-gray-900">
                        {formatCurrency(order.totalAmount)}
                      </span>
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700 transition-transform ${
                          (expandedOrders[order.id] ?? !isMobileView) ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      >
                        ▼
                      </span>
                    </div>
                  </div>
                </button>

                {/* Items del pedido */}
                {(expandedOrders[order.id] ?? !isMobileView) && (
                  <div className="p-4 sm:p-6">
                    <div className="space-y-3">
                      {order.items.map((item) => (
                        <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {item.menuItem.name}
                              {item.size && (
                                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                                  {item.size}
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600">
                              Cantidad: {item.quantity}
                            </p>
                            {item.specialInstructions && (
                              <p className="text-sm text-gray-500 italic mt-1">
                                Notas: {item.specialInstructions}
                              </p>
                            )}
                          </div>
                          <span className="font-medium text-gray-900 flex-shrink-0">
                            {formatCurrency(item.price)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Información adicional */}
                {(expandedOrders[order.id] ?? !isMobileView) && (
                  <div className="bg-gray-50 p-4 sm:p-6 border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {order.estimatedDelivery && (
                        <div>
                          <span className="text-gray-600">Entrega estimada:</span>
                          <p className="text-gray-900">{formatDate(order.estimatedDelivery)}</p>
                        </div>
                      )}
                      
                      {order.rating && (
                        <div>
                          <span className="text-gray-600">Calificación:</span>
                          <p className="text-gray-900">⭐ {order.rating || 0}/5</p>
                        </div>
                      )}
                    </div>
                    
                    {order.notes && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span className="text-gray-600 text-sm">Notas del pedido:</span>
                        <p className="text-gray-900 text-sm mt-1">{order.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <ClientTablesReservationModal
        visible={showTablesModal}
        onClose={() => setShowTablesModal(false)}
        restaurantId={restaurantId || ''}
        restaurantName={restaurant?.name || ''}
        onReservationCreated={(text) => setPageAlert({ type: 'success', text })}
      />

      {/* Modal Próximamente */}
      
    </div>
  );
};

export default OrdersHistoryPage; 